import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { adaptReviewToUI } from 'src/adapters/storeToUI/adaptReviewToUI';
import type { CustomersReviewsPreviewProps } from 'src/components/organisms/CustomersReviewsPreview/components/CustomersReviewsPreview';
import { PREVIEW_REVIEW_COUNT } from 'src/components/organisms/CustomersReviewsPreview/constants/previewReviewCount';
import {
  trackPreviewImpression,
  trackPreviewScroll,
  trackReviewCardControlClick,
} from 'src/components/organisms/CustomersReviewsPreview/tracking/customersReviewsPreviewEvents';
import { getReviewsDrawerHref } from 'src/fragmentIDs';
import { selectProductPartner } from 'src/redux/product/selectors';
import {
  selectFirstReviewsPage,
  selectReviewsListError,
  selectReviewsTotalCount,
} from 'src/redux/reviews/selectors';

/**
 * View model for the reviews preview: reads across the product and reviews slices, maps
 * into the design system's `Review` and wires the tracking, so the carousel stays pure props.
 */
export const useCustomersReviewsPreviewViewModel = (): CustomersReviewsPreviewProps => {
  // Narrow subscriptions: the whole `list` changes on every drawer page turn, and this
  // hook sits in the page layout.
  const firstPage = useSelector(selectFirstReviewsPage);
  const error = useSelector(selectReviewsListError);
  const totalCount = useSelector(selectReviewsTotalCount);
  const partner = useSelector(selectProductPartner);

  const reviews = useMemo(
    () =>
      firstPage?.slice(0, PREVIEW_REVIEW_COUNT).map((review) => adaptReviewToUI(review, partner)) ??
      null,
    [firstPage, partner],
  );

  return {
    // The list error is set by any page's failure and the drawer pages the same list, so a
    // later page failing must not replace a first page the preview already holds.
    error: error && firstPage === undefined,
    href: getReviewsDrawerHref(),
    hrefForReview: getReviewsDrawerHref,
    onControlClick: (_e, { control }) => trackReviewCardControlClick(control),
    onScroll: trackPreviewScroll,
    onVisible: trackPreviewImpression,
    reviews,
    // The page's single source for the count, shared with the title and the drawer.
    totalCount,
  };
};
